import { useState } from 'react';
import { Modal } from '@adiox/ui';
import { APPLIED_PRICES, MA_METHODS } from './math';
import { indicatorById, normalizeParams, type IndicatorDef, type ParamDef, type Params } from './library';
import { MAX_LEVELS, type IndicatorInstance, type OutputStyle } from './instances';

const APPLIED_LABELS: Record<string, string> = {
  close: 'Close',
  open: 'Open',
  high: 'High',
  low: 'Low',
  median: 'Median price (HL/2)',
  typical: 'Typical price (HLC/3)',
  weighted: 'Weighted close (HLCC/4)',
  prev: "Previous indicator's data",
  first: "First indicator's data",
};
const METHOD_LABELS: Record<string, string> = {
  sma: 'Simple',
  ema: 'Exponential',
  smma: 'Smoothed',
  lwma: 'Linear weighted',
};

const field =
  'w-full rounded-md border border-line bg-panel2 px-2 py-1 text-sm outline-none focus:border-accent';
const label = 'mb-1 block text-xs uppercase tracking-wide text-muted';

/** MT5-style dialog for one indicator: its parameters on one tab, the colour, width and visibility of its lines on the other. */
export function IndicatorDialog({
  editing,
  hasEarlier = false,
  onSave,
  onRemove,
  onClose,
}: {
  /** The instance being added or edited; null when the dialog is closed. */
  editing: { instance: IndicatorInstance; isNew: boolean } | null;
  /** Another indicator comes before this one on the chart, so "Apply to" may offer its data. */
  hasEarlier?: boolean;
  onSave: (instance: IndicatorInstance) => void;
  onRemove: (uid: string) => void;
  onClose: () => void;
}) {
  const def = editing ? indicatorById(editing.instance.type) : undefined;
  return (
    <Modal
      size="md"
      open={editing !== null && def !== undefined}
      title={def ? `${def.name}${editing?.isNew ? '' : ' (edit)'}` : ''}
      onClose={onClose}
    >
      {editing && def && (
        <Form
          key={editing.instance.uid}
          def={def}
          instance={editing.instance}
          isNew={editing.isNew}
          hasEarlier={hasEarlier}
          onSave={onSave}
          onRemove={onRemove}
          onClose={onClose}
        />
      )}
    </Modal>
  );
}

function Form({
  def,
  instance,
  isNew,
  hasEarlier,
  onSave,
  onRemove,
  onClose,
}: {
  def: IndicatorDef;
  instance: IndicatorInstance;
  isNew: boolean;
  hasEarlier: boolean;
  onSave: (instance: IndicatorInstance) => void;
  onRemove: (uid: string) => void;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<'parameters' | 'style' | 'levels'>('parameters');
  // Levels are edited as text, one per line, so any number of them can be typed and pasted.
  const [levels, setLevels] = useState((instance.levels ?? def.levels ?? []).join('\n'));
  const parsedLevels = levels
    .split(/[\n,;]+/)
    .map((l) => l.trim())
    .filter((l) => l !== '');
  const levelProblem = parsedLevels.some((l) => !Number.isFinite(Number(l)))
    ? 'Each level must be a number.'
    : parsedLevels.length > MAX_LEVELS
      ? `Up to ${MAX_LEVELS} levels.`
      : null;
  // Numbers are kept as the text being typed, so a half-typed value is not rewritten under the cursor.
  const [values, setValues] = useState<Record<string, string>>(
    Object.fromEntries(def.params.map((p) => [p.key, String(instance.params[p.key] ?? p.default)])),
  );
  const [styles, setStyles] = useState<Record<string, OutputStyle>>(instance.styles);

  const paramProblems = def.params.flatMap((p) => {
    if (p.type !== 'int' && p.type !== 'float') return [];
    const n = Number(values[p.key]);
    if (!Number.isFinite(n) || values[p.key]!.trim() === '') return [`${p.label} must be a number.`];
    if (n < p.min || n > p.max) return [`${p.label} must be between ${p.min} and ${p.max}.`];
    if (p.type === 'int' && !Number.isInteger(n)) return [`${p.label} must be a whole number.`];
    return [];
  });
  const problems = levelProblem ? [...paramProblems, levelProblem] : paramProblems;

  const save = () => {
    if (problems.length > 0) return;
    const params: Params = normalizeParams(def, values);
    const chosen = parsedLevels.map(Number);
    const own = def.levels ?? [];
    const same = chosen.length === own.length && chosen.every((l, i) => l === own[i]);
    const { levels: _old, ...rest } = instance;
    onSave(same ? { ...rest, params, styles } : { ...rest, params, styles, levels: chosen });
    onClose();
  };

  const control = (p: ParamDef) => {
    const id = `ind-${p.key}`;
    if (p.type === 'method' || p.type === 'applied') {
      const options: readonly string[] =
        p.type === 'method'
          ? MA_METHODS
          : def.sourceable && hasEarlier
            ? [...APPLIED_PRICES, 'prev', 'first']
            : APPLIED_PRICES;
      const labels = p.type === 'method' ? METHOD_LABELS : APPLIED_LABELS;
      return (
        <select
          id={id}
          className={field}
          value={values[p.key]}
          onChange={(e) => setValues({ ...values, [p.key]: e.target.value })}
        >
          {options.map((o) => (
            <option key={o} value={o}>
              {labels[o]}
            </option>
          ))}
        </select>
      );
    }
    return (
      <input
        id={id}
        type="number"
        inputMode="decimal"
        className={`${field} num`}
        step={p.type === 'float' ? p.step : 1}
        min={p.min}
        max={p.max}
        value={values[p.key]}
        onChange={(e) => setValues({ ...values, [p.key]: e.target.value })}
      />
    );
  };

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
      noValidate
    >
      <div role="tablist" aria-label="Indicator settings" className="flex gap-1 border-b border-line">
        {(def.overlay
          ? (['parameters', 'style'] as const)
          : (['parameters', 'style', 'levels'] as const)
        ).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={`px-3 py-1 text-sm capitalize ${tab === t ? 'border-b-2 border-accent font-semibold' : 'text-muted'}`}
          >
            {t === 'style' ? 'Colors' : t === 'levels' ? 'Levels' : 'Parameters'}
          </button>
        ))}
      </div>

      {tab === 'parameters' && (
        <div className="flex flex-col gap-3" role="tabpanel" aria-label="Parameters">
          {def.params.length === 0 && <p className="text-sm text-muted">This indicator has no parameters.</p>}
          {def.params.map((p) => (
            <div key={p.key}>
              <label className={label} htmlFor={`ind-${p.key}`}>
                {p.label}
              </label>
              {control(p)}
            </div>
          ))}
        </div>
      )}

      {tab === 'style' && (
        <div className="flex flex-col gap-2" role="tabpanel" aria-label="Colors">
          {def.outputs.map((o) => {
            const style = styles[o.key]!;
            const update = (patch: Partial<OutputStyle>) =>
              setStyles({ ...styles, [o.key]: { ...style, ...patch } });
            return (
              <div key={o.key} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  aria-label={`Show ${o.label}`}
                  checked={style.visible}
                  onChange={(e) => update({ visible: e.target.checked })}
                  className="accent-[var(--accent)]"
                />
                <span className="min-w-0 flex-1 truncate">{o.label}</span>
                <input
                  type="color"
                  aria-label={`${o.label} color`}
                  value={style.color}
                  onChange={(e) => update({ color: e.target.value })}
                  className="h-7 w-10 cursor-pointer rounded border border-line bg-transparent"
                />
                {o.style !== 'histogram' && (
                  <select
                    aria-label={`${o.label} width`}
                    className="rounded-md border border-line bg-panel2 px-1 py-1 text-sm"
                    value={style.width}
                    onChange={(e) => update({ width: Number(e.target.value) as 1 | 2 | 3 })}
                  >
                    {[1, 2, 3].map((w) => (
                      <option key={w} value={w}>
                        {w}px
                      </option>
                    ))}
                  </select>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tab === 'levels' && (
        <div className="flex flex-col gap-2" role="tabpanel" aria-label="Levels">
          <label className={label} htmlFor="ind-levels">
            Levels (one per line)
          </label>
          <textarea
            id="ind-levels"
            rows={5}
            className={`${field} num`}
            value={levels}
            onChange={(e) => setLevels(e.target.value)}
          />
          <p className="text-xs text-muted">
            Dotted reference lines in the indicator's window.{' '}
            <button
              type="button"
              className="text-accent underline-offset-2 hover:underline"
              onClick={() => setLevels((def.levels ?? []).join('\n'))}
            >
              Reset to {def.levels && def.levels.length > 0 ? def.levels.join(', ') : 'none'}
            </button>
          </p>
        </div>
      )}

      <div role="alert" className="min-h-[1.25rem] text-sm text-down">
        {problems[0]}
      </div>
      <div className="flex items-center justify-between gap-2">
        {!isNew ? (
          <button
            type="button"
            className="rounded border border-down px-3 py-1 text-sm text-down hover:bg-down/10"
            onClick={() => {
              onRemove(instance.uid);
              onClose();
            }}
          >
            Remove
          </button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <button
            type="button"
            className="rounded border border-line px-3 py-1 text-sm hover:border-accent"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={problems.length > 0}
            className="rounded-md bg-accent px-3 py-1 text-sm font-semibold text-accent-fg disabled:opacity-40"
          >
            {isNew ? 'Add' : 'OK'}
          </button>
        </div>
      </div>
    </form>
  );
}
